import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plug,
  Plus,
  Trash2,
  Power,
  PowerOff,
  TestTube,
  Loader2,
  ExternalLink,
  Store,
  Download,
  Check,
  Search,
  Sparkles,
  Globe,
  Code,
  Zap,
  X,
  Mic,
  Image,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const REQUEST_FORMATS = [
  { value: "openai", label: "OpenAI-compatible" },
  { value: "anthropic", label: "Anthropic-compatible" },
  { value: "custom", label: "Custom" },
];

const PRESET_COLORS = [
  "#6B7280", "#10A37F", "#F97316", "#1D4ED8", "#4338CA",
  "#DC2626", "#D946EF", "#0891B2", "#65A30D", "#CA8A04"
];

const CATEGORY_ICONS = {
  inference: Zap,
  provider: Sparkles,
  search: Globe,
  aggregator: Globe,
  specialized: Code,
};

const CATEGORY_LABELS = {
  inference: "Inference",
  provider: "Provider",
  search: "Search-Augmented",
  aggregator: "Aggregator",
  specialized: "Specialized",
};

export default function PluginsPage({ api }) {
  const [providers, setProviders] = useState([]);
  const [marketplace, setMarketplace] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketplaceLoading, setMarketplaceLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [testing, setTesting] = useState(null);
  const [installing, setInstalling] = useState(null);
  const [uninstalling, setUninstalling] = useState(null);
  const [marketplaceSearch, setMarketplaceSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("marketplace");

  // Form state
  const [form, setForm] = useState({
    slug: "", name: "", api_base_url: "",
    api_key_header: "Authorization", api_key_prefix: "Bearer ",
    capabilities: ["text"], request_format: "openai", accent: "#6B7280",
    billing_url: "", docs_url: "",
    models: [{ id: "", name: "", input_cost: 0, output_cost: 0 }],
  });

  const fetchProviders = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/plugins/providers`);
      setProviders(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchMarketplace = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/plugins/marketplace`);
      setMarketplace(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setMarketplaceLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchProviders(); fetchMarketplace(); }, [fetchProviders, fetchMarketplace]);

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const updateModel = (idx, key, val) => {
    setForm(prev => {
      const models = [...prev.models];
      models[idx] = { ...models[idx], [key]: val };
      return { ...prev, models };
    });
  };

  const addModel = () => {
    setForm(prev => ({
      ...prev,
      models: [...prev.models, { id: "", name: "", input_cost: 0, output_cost: 0 }],
    }));
  };

  const removeModel = (idx) => {
    setForm(prev => ({
      ...prev,
      models: prev.models.filter((_, i) => i !== idx),
    }));
  };

  const handleCreate = async () => {
    if (!form.slug.trim() || !form.name.trim() || !form.api_base_url.trim()) {
      toast.error("Slug, name, and API base URL are required");
      return;
    }
    const validModels = form.models.filter(m => m.id.trim() && m.name.trim());
    try {
      await axios.post(`${api}/plugins/providers`, { ...form, models: validModels });
      toast.success("Custom provider created!");
      setCreateOpen(false);
      setForm({
        slug: "", name: "", api_base_url: "",
        api_key_header: "Authorization", api_key_prefix: "Bearer ",
        capabilities: ["text"], request_format: "openai", accent: "#6B7280",
        billing_url: "", docs_url: "",
        models: [{ id: "", name: "", input_cost: 0, output_cost: 0 }],
      });
      fetchProviders();
      fetchMarketplace();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create provider");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${api}/plugins/providers/${deleteId}`);
      toast.success("Provider deleted");
      setDeleteId(null);
      fetchProviders();
      fetchMarketplace();
    } catch (err) {
      toast.error("Failed to delete provider");
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.post(`${api}/plugins/providers/${id}/toggle`);
      toast.success(res.data.enabled ? "Provider enabled" : "Provider disabled");
      fetchProviders();
    } catch (err) {
      toast.error("Failed to toggle provider");
    }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await axios.post(`${api}/plugins/providers/${id}/test`);
      if (res.data.success) {
        toast.success(`Test passed! Latency: ${res.data.latency}s`);
      } else {
        toast.error(res.data.message || "Test failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally {
      setTesting(null);
    }
  };

  const handleInstall = async (slug) => {
    setInstalling(slug);
    try {
      const res = await axios.post(`${api}/plugins/marketplace/install/${slug}`);
      toast.success(res.data.message);
      fetchProviders();
      fetchMarketplace();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Install failed");
    } finally {
      setInstalling(null);
    }
  };

  const handleUninstall = async (slug) => {
    setUninstalling(slug);
    try {
      await axios.post(`${api}/plugins/marketplace/uninstall/${slug}`);
      toast.success(`Provider uninstalled`);
      fetchProviders();
      fetchMarketplace();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uninstall failed");
    } finally {
      setUninstalling(null);
    }
  };

  const filteredMarketplace = marketplace.filter(p => {
    const matchesSearch = !marketplaceSearch ||
      p.name.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
      p.description.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
      p.slug.toLowerCase().includes(marketplaceSearch.toLowerCase());
    const matchesCategory = categoryFilter === "all" || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = ["all", ...new Set(marketplace.map(p => p.category).filter(Boolean))];

  return (
    <div className="main-content-inner" data-testid="plugins-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2.5">
            <Plug className="w-6 h-6 text-primary" /> Providers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Browse the marketplace or register custom LLM providers
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="marketplace" className="gap-1.5" data-testid="marketplace-tab">
              <Store className="w-3.5 h-3.5" /> Marketplace
            </TabsTrigger>
            <TabsTrigger value="installed" className="gap-1.5" data-testid="installed-tab">
              <Plug className="w-3.5 h-3.5" /> Installed
              {providers.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 text-[10px] px-1.5">{providers.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          {activeTab === "installed" && (
            <Button onClick={() => setCreateOpen(true)} data-testid="add-provider-btn" size="sm" className="gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Custom Provider
            </Button>
          )}
        </div>

        {/* ─── Marketplace Tab ────────────────────────────────────── */}
        <TabsContent value="marketplace" className="mt-0">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search providers..."
              value={marketplaceSearch}
              onChange={e => setMarketplaceSearch(e.target.value)}
              data-testid="marketplace-search"
            />
            {marketplaceSearch && (
              <Button
                variant="ghost" size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setMarketplaceSearch("")}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {/* Category filter pills */}
          <div className="flex items-center gap-1.5 mb-4 flex-wrap">
            {categories.map(cat => (
              <Button
                key={cat}
                variant={categoryFilter === cat ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs capitalize"
                onClick={() => setCategoryFilter(cat)}
                data-testid={`filter-${cat}`}
              >
                {cat === "all" ? `All (${marketplace.length})` : `${CATEGORY_LABELS[cat] || cat} (${marketplace.filter(p => p.category === cat).length})`}
              </Button>
            ))}
          </div>

          {marketplaceLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <Card key={i}><CardContent className="p-6"><div className="h-36 bg-muted rounded-lg animate-pulse" /></CardContent></Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMarketplace.map(item => {
                const CatIcon = CATEGORY_ICONS[item.category] || Sparkles;
                return (
                  <Card
                    key={item.slug}
                    className="relative overflow-hidden group transition-all hover:shadow-md"
                    style={{ borderTop: `3px solid ${item.accent}` }}
                    data-testid="marketplace-provider-card"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-sm"
                            style={{ backgroundColor: item.accent }}
                          >
                            <Plug className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold leading-tight">{item.name}</h3>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Badge variant="outline" className="text-[9px] h-4 gap-0.5 font-normal">
                                <CatIcon className="w-2 h-2" /> {CATEGORY_LABELS[item.category] || item.category}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {item.installed && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] gap-0.5">
                            <Check className="w-2.5 h-2.5" /> Installed
                          </Badge>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
                        {item.description}
                      </p>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {item.models?.slice(0, 3).map(m => (
                          <Badge key={m.id} variant="secondary" className="text-[9px] h-4 font-mono">
                            {m.name}
                          </Badge>
                        ))}
                        {(item.model_count || item.models?.length || 0) > 3 && (
                          <Badge variant="secondary" className="text-[9px] h-4">
                            +{(item.model_count || item.models?.length || 0) - 3} more
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {item.installed ? (
                          <Button
                            variant="outline" size="sm" className="h-7 text-xs flex-1 gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleUninstall(item.slug)}
                            disabled={uninstalling === item.slug}
                            data-testid="uninstall-provider-btn"
                          >
                            {uninstalling === item.slug ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                            Uninstall
                          </Button>
                        ) : (
                          <Button
                            size="sm" className="h-7 text-xs flex-1 gap-1"
                            onClick={() => handleInstall(item.slug)}
                            disabled={installing === item.slug}
                            data-testid="install-provider-btn"
                          >
                            {installing === item.slug ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3" />
                            )}
                            Install
                          </Button>
                        )}
                        {item.docs_url && (
                          <a href={item.docs_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ─── Installed Tab ──────────────────────────────────────── */}
        <TabsContent value="installed" className="mt-0">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1,2].map(i => (
                <Card key={i}><CardContent className="p-6"><div className="h-24 bg-muted rounded animate-pulse" /></CardContent></Card>
              ))}
            </div>
          ) : providers.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Plug className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium">No custom providers installed</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  Browse the Marketplace to one-click install popular providers, or add a custom provider manually.
                </p>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button onClick={() => setActiveTab("marketplace")} variant="outline" className="gap-2">
                    <Store className="w-4 h-4" /> Browse Marketplace
                  </Button>
                  <Button onClick={() => setCreateOpen(true)} className="gap-2">
                    <Plus className="w-4 h-4" /> Custom Provider
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* How-to tip */}
              <Card className="border-amber-200/50 bg-amber-50/40">
                <CardContent className="p-3 flex items-start gap-2.5">
                  <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800">
                    After installing a provider, go to <strong>API Keys</strong> page to add its API key.
                  </p>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {providers.map(p => (
                  <Card key={p.id} className="relative" style={{ borderLeft: `4px solid ${p.accent}` }} data-testid="custom-provider-card">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: p.accent }}>
                            <Plug className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold">{p.name}</h3>
                            <p className="text-[11px] text-muted-foreground font-mono">{p.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {p.source === "marketplace" && (
                            <Badge variant="outline" className="text-[9px] h-4 gap-0.5">
                              <Store className="w-2 h-2" /> Marketplace
                            </Badge>
                          )}
                          <Badge variant={p.enabled ? "default" : "secondary"} className="text-[10px]">
                            {p.enabled ? "Active" : "Disabled"}
                          </Badge>
                        </div>
                      </div>

                      {p.description && (
                        <p className="text-[11px] text-muted-foreground mt-2 line-clamp-1">{p.description}</p>
                      )}

                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge variant="outline" className="text-[10px]">{p.request_format}</Badge>
                        {p.capabilities?.map(c => (
                          <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>
                        ))}
                        <Badge variant="outline" className="text-[10px]">{p.models?.length || 0} models</Badge>
                      </div>

                      <p className="text-[11px] text-muted-foreground mt-2 font-mono truncate">{p.api_base_url}</p>

                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          variant="outline" size="sm" className="h-7 text-xs gap-1"
                          onClick={() => handleToggle(p.id)}
                        >
                          {p.enabled ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                          {p.enabled ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          variant="outline" size="sm" className="h-7 text-xs gap-1"
                          onClick={() => handleTest(p.id)}
                          disabled={testing === p.id}
                          data-testid="test-provider-btn"
                        >
                          {testing === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <TestTube className="w-3 h-3" />}
                          Test
                        </Button>
                        {p.docs_url && (
                          <a href={p.docs_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1">
                              <ExternalLink className="w-3 h-3" /> Docs
                            </Button>
                          </a>
                        )}
                        <div className="flex-1" />
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(p.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register Custom Provider</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Slug (unique ID)</Label>
                <Input placeholder="e.g. together-ai" value={form.slug} onChange={e => updateForm("slug", e.target.value)} data-testid="plugin-slug-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Display Name</Label>
                <Input placeholder="e.g. Together AI" value={form.name} onChange={e => updateForm("name", e.target.value)} data-testid="plugin-name-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">API Base URL</Label>
              <Input placeholder="https://api.together.xyz/v1" value={form.api_base_url} onChange={e => updateForm("api_base_url", e.target.value)} data-testid="plugin-url-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">API Key Header</Label>
                <Input value={form.api_key_header} onChange={e => updateForm("api_key_header", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">API Key Prefix</Label>
                <Input value={form.api_key_prefix} onChange={e => updateForm("api_key_prefix", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Request Format</Label>
                <Select value={form.request_format} onValueChange={v => updateForm("request_format", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REQUEST_FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Color</Label>
                <div className="flex gap-1.5 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => updateForm("accent", c)}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${form.accent === c ? "border-foreground scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Billing URL (optional)</Label>
                <Input value={form.billing_url} onChange={e => updateForm("billing_url", e.target.value)} placeholder="https://..." />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Docs URL (optional)</Label>
                <Input value={form.docs_url} onChange={e => updateForm("docs_url", e.target.value)} placeholder="https://..." />
              </div>
            </div>

            {/* Models */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold">Models</Label>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={addModel}>
                  <Plus className="w-3 h-3 mr-1" /> Add Model
                </Button>
              </div>
              <div className="space-y-2">
                {form.models.map((m, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      {idx === 0 && <Label className="text-[10px] text-muted-foreground">Model ID</Label>}
                      <Input className="h-8 text-xs" placeholder="model-id" value={m.id} onChange={e => updateModel(idx, "id", e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      {idx === 0 && <Label className="text-[10px] text-muted-foreground">Name</Label>}
                      <Input className="h-8 text-xs" placeholder="Name" value={m.name} onChange={e => updateModel(idx, "name", e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-[10px] text-muted-foreground">In $/1M</Label>}
                      <Input className="h-8 text-xs" type="number" step="0.01" value={m.input_cost} onChange={e => updateModel(idx, "input_cost", parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-[10px] text-muted-foreground">Out $/1M</Label>}
                      <Input className="h-8 text-xs" type="number" step="0.01" value={m.output_cost} onChange={e => updateModel(idx, "output_cost", parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="col-span-1">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => removeModel(idx)} disabled={form.models.length <= 1}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-provider-submit">Create Provider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Provider</AlertDialogTitle>
            <AlertDialogDescription>This will remove the provider and its stored API key.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
